import { fail, success } from '@/helpers/result-helper'
import toast from '@/services/toasts/index'
import { isSuccess, isHttpStatus } from '@/helpers/http-status-helpers'
import { orderBy } from 'lodash'
import BookingBlockViewModel from '@/models/locations/bookingBlockViewModel'
import { isNonEmptyArray } from '@/helpers/array-helpers'

const getDefaultState = () => {
  return {
    // Place any new state properties here
    bookingLocations: [],
    certifications: [],
    locationRestrictions: [],
    loadingCount: 0,
    crudLoadingCount: 0,
    bookingLocationLoadingCount: 0,
    certificationLoadingCount: 0,
    bookingBlocksLoadingCount: 0,
  }
}

const state = getDefaultState()

export default {
  namespaced: true,
  state,
  getters: {
    moduleName: () => 'locations',
    bookingLocations: (state) => state.bookingLocations,
    certifications: (state) => (locationId) => {
      const certifications = state.certifications.find(
        (x) => x.locationId === locationId
      )

      return certifications
        ? orderBy(certifications.list, ['name'], ['asc'])
        : []
    },
    isLoading: (state) => state.loadingCount > 0,
    isLoadingCRUD: (state) => state.crudLoadingCount > 0,
    isLoadingCertifications: (state) => state.certificationLoadingCount > 0,
    isLoadingBookingLocations: (state) => state.bookingLocationLoadingCount > 0,
    isLoadingBookingBlocks: (state) => state.bookingBlocksLoadingCount > 0,
    getRestrictionDetailsByLocationId: (state) => (locationId) => {
      if (!locationId || locationId < 1) throw Error('Location Id is required')

      return state.locationRestrictions?.find(
        (restriction) => restriction.locationId === locationId
      )
    },
  },
  mutations: {
    START_LOADING(state) {
      state.loadingCount++
    },
    FINISH_LOADING(state) {
      state.loadingCount--
    },
    START_LOADING_CRUD(state) {
      state.crudLoadingCount++
    },
    FINISH_LOADING_CRUD(state) {
      state.crudLoadingCount--
    },
    START_LOADING_BOOKING_LOCATIONS(state) {
      state.bookingLocationLoadingCount++
    },
    FINISH_LOADING_BOOKING_LOCATIONS(state) {
      state.bookingLocationLoadingCount--
    },
    START_LOADING_CERTIFICATIONS(state) {
      state.certificationLoadingCount++
    },
    FINISH_LOADING_CERTIFICATIONS(state) {
      state.certificationLoadingCount--
    },
    START_LOADING_BOOKING_BLOCKS(state) {
      state.bookingBlocksLoadingCount++
    },
    FINISH_LOADING_BOOKING_BLOCKS(state) {
      state.bookingBlocksLoadingCount--
    },
    SET_BOOKING_LOCATIONS(state, payload) {
      state.bookingLocations = payload
    },
    UPSERT_CERTIFICATIONS(state, payload) {
      const found = state.certifications.find(
        (x) => x.locationId === payload.locationId
      )

      if (!found) {
        return state.certifications.push({
          locationId: payload.locationId,
          list: payload.certifications,
        })
      }

      found.list = payload.certifications
    },
    SET_LOCATION_RESTRICTIONS(state, restrictions) {
      state.locationRestrictions = isNonEmptyArray(restrictions)
        ? restrictions.map(
            (restriction) => new BookingBlockViewModel(restriction)
          )
        : []
    },
    CLEAR_STORE(state) {
      // Resets store to default state
      Object.assign(state, getDefaultState())
    },
  },
  actions: {
    async loadBookingLocations({ commit, getters }) {
      const isCached = getters.bookingLocations

      if (isCached && isCached.length > 0)
        return Promise.resolve(success(isCached))

      commit('START_LOADING_BOOKING_LOCATIONS')

      try {
        const response = await this.$api.locations.getBookingLocations()

        if (isSuccess(response.status)) {
          commit(
            'SET_BOOKING_LOCATIONS',
            isHttpStatus(response.status, 204) ? [] : response.data
          )
          return success(response.data)
        }
      } catch (ex) {
        toast.error('Cannot load locations')
        return fail()
      } finally {
        commit('FINISH_LOADING_BOOKING_LOCATIONS')
      }
    },
    async loadLocationCertifications({ commit, getters }, locationId) {
      const isCached = getters.certifications(locationId)

      if (isCached && isCached.length > 0)
        return Promise.resolve(success(isCached))

      commit('START_LOADING_CERTIFICATIONS')

      try {
        const response = await this.$api.locations.getLocationCertifications(
          locationId
        )

        if (isSuccess(response.status)) {
          commit('UPSERT_CERTIFICATIONS', {
            certifications: isHttpStatus(response.status, 204)
              ? []
              : response.data,
            locationId,
          })
          return success(response.data)
        }
      } catch (ex) {
        toast.error('Cannot load certifications for this location')
        return fail()
      } finally {
        commit('FINISH_LOADING_CERTIFICATIONS')
      }
    },
    /**
     * Loads the booking blocks for all contact locations
     * booking on a particular day
     * @param {{commit: Function, dispatch: Function}} vuexContext
     * @returns
     */
    async loadLocationBookingBlocks({ commit }) {
      commit('START_LOADING_BOOKING_BLOCKS')

      try {
        const response = await this.$api.locations.getLocationBookingBlocks()

        if (isSuccess(response.status)) {
          commit('SET_LOCATION_RESTRICTIONS', response.data)
          return success(response.data)
        }
      } catch (ex) {
        return fail()
      } finally {
        commit('FINISH_LOADING_BOOKING_BLOCKS')
      }
    },
    /**
     * Resets store to default state.
     */
    clear({ commit }) {
      commit('CLEAR_STORE')
    },
  },
}
